import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../Button';
import { SendIcon } from '../Button/icons';
import './ChatInput.css';

interface ChatInputProps {
  onSend: (message: string) => void;
  placeholder?: string;
  disabled?: boolean;
  disableInput?: boolean;
  disableSend?: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  placeholder = "I'm looking for...",
  disabled = false,
  disableInput,
  disableSend,
}) => {
  const [message, setMessage] = useState('');
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const shouldRefocusRef = useRef(false);
  const isInputDisabled = disableInput ?? disabled;
  const isSendDisabled = disableSend ?? disabled;

  const resizeInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;

    const isDesktop =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(min-width: 768px)').matches
        : true;
    const minHeight = isDesktop ? 64 : 44;
    const maxHeight = isDesktop ? 200 : 120;

    input.style.height = 'auto';
    const nextHeight = Math.min(maxHeight, Math.max(minHeight, input.scrollHeight));
    input.style.height = `${nextHeight}px`;
    input.style.overflowY = input.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  const handleSend = () => {
    if (isSendDisabled) return;

    const trimmedMessage = message.trim();
    if (trimmedMessage) {
      shouldRefocusRef.current = true;
      onSend(trimmedMessage);
      setMessage('');
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  useEffect(() => {
    resizeInput();
  }, [message, resizeInput]);

  useEffect(() => {
    if (!isSendDisabled && shouldRefocusRef.current) {
      shouldRefocusRef.current = false;
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isSendDisabled]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.addEventListener('resize', resizeInput);
    return () => window.removeEventListener('resize', resizeInput);
  }, [resizeInput]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat-input-container">
      <textarea
        ref={inputRef}
        className="chat-input-field"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
        placeholder={placeholder}
        rows={1}
        disabled={isInputDisabled}
        aria-disabled={isInputDisabled}
        aria-label="Chat message"
      />
      <div className="chat-input-actions">
        <div
          onMouseDown={(event) => {
            if (event.button === 0 && !isSendDisabled) {
              event.preventDefault();
            }
          }}
        >
          <Button
            type="primary"
            size="medium"
            isIconOnly={true}
            onClick={handleSend}
            disabled={isSendDisabled || !message.trim()}
            aria-label="Send message"
            className="chat-input-send-button"
            icon={<SendIcon />}
          >
            <SendIcon />
          </Button>
        </div>
      </div>
    </div>
  );
};
